import { useEffect, useState, type ReactNode } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  useGetCompanyQuery,
  useGetSiteQuery,
  useGetWellQuery,
  type RrcCompanyProfile,
  type RrcLocationProfile,
} from '@ots-frontend/ots-data';
import { OtsDialog } from '../components/OtsDialog';
import { OtsInlineAlert } from '../components/OtsInlineAlert';
import { OtsActionButton } from '../components/OtsButton';
import { OtsDataTableEmpty } from '../components/OtsDataTableCells';
import {
  companyProfileDefaults,
  locationProfileDefaults,
  mergeProfileDefaults,
} from './rrc-profile-defaults';

type Draft = Partial<RrcCompanyProfile> | Partial<RrcLocationProfile>;
type Props<T extends Draft> = {
  type: 'COMPANY' | 'SITE' | 'WELL';
  id: string;
  regionId: string;
  defaults?: T;
  presentation?: 'dialog' | 'embedded';
  compact?: boolean;
  onClose?: () => void;
  children: (draft: T) => ReactNode;
};
/** Mount the editor once defaults resolve; background refreshes cannot overwrite a user's draft. */
export function RrcProfilePrefill<T extends Draft>({
  type,
  id,
  regionId,
  defaults,
  presentation = 'dialog',
  compact,
  onClose,
  children,
}: Props<T>) {
  const [snapshot, setSnapshot] = useState<T>();
  const company = useGetCompanyQuery(
    type === 'COMPANY' && !snapshot ? id : skipToken
  );
  const site = useGetSiteQuery(type === 'SITE' && !snapshot ? id : skipToken);
  const well = useGetWellQuery(type === 'WELL' && !snapshot ? id : skipToken);
  const query = type === 'COMPANY' ? company : type === 'SITE' ? site : well;
  useEffect(() => {
    if (snapshot) return;
    const seed =
      type === 'COMPANY' && company.currentData?.id === id
        ? companyProfileDefaults(company.currentData, regionId)
        : type === 'SITE' && site.currentData?.id === id
        ? locationProfileDefaults(site.currentData, 'SITE')
        : type === 'WELL' && well.currentData?.id === id
        ? locationProfileDefaults(well.currentData, 'WELL')
        : undefined;
    if (seed && !query.isError)
      setSnapshot(mergeProfileDefaults(seed, defaults) as T);
  }, [
    company.currentData,
    site.currentData,
    well.currentData,
    type,
    id,
    regionId,
    defaults,
    snapshot,
    query.isError,
  ]);
  if (snapshot) return <>{children(snapshot)}</>;
  const content = query.isError ? (
    <OtsInlineAlert title="Record details unavailable" tone="warning">
      You can retry or enter the profile details yourself.
      <div className="mt-3 flex gap-2">
        <OtsActionButton onClick={() => void query.refetch()}>
          Retry
        </OtsActionButton>
        <OtsActionButton onClick={() => setSnapshot({ ...defaults } as T)}>
          Continue without prefilling
        </OtsActionButton>
      </div>
    </OtsInlineAlert>
  ) : (
    <OtsDataTableEmpty loading loadingText="Loading record details…">
      {null}
    </OtsDataTableEmpty>
  );
  return presentation === 'dialog' ? (
    <OtsDialog open title="New RRC profile" compact={compact} onClose={onClose}>
      {content}
    </OtsDialog>
  ) : (
    <div>{content}</div>
  );
}
